import type { FormEvent } from 'react';
import React, { useState } from 'react';
import { range } from 'lodash-es';
import styled from 'styled-components';

import {
  AdornedInput,
  Adornment,
  FormField,
  FormHelp,
  IconPlusOutline,
  Input,
  Label,
  Paragraph,
  Spacer,
  Text,
  TextButton,
} from '@zillow/constellation';
import { Center, Col, CurrencyInput, Row } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import { Field, NonRefundableFeesSection } from '../../../models/index';
import { NON_REFUNDABLE_FEES } from '../../../constants/index';
import {
  CURRENCY_INPUT_PLACEHOLDER,
  FEE_PAYMENT_TIME,
  MAX_AMOUNT_ERROR_MESSAGE,
} from '../../../constants/stringConstants';
import YesAndNo from '../../Widgets/YesAndNo';
import { CenteredHR } from '../../Widgets/CenteredHR';
import FeePaymentTime from '../../Widgets/FeePaymentTime';
import { isYesNoEnabledForNumberedFields } from '../../../constants/functionConstants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_FEES = 5;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  height: 1rem;
`;

type Props = {
  section: NonRefundableFeesSection;
  setSectionValues: (object) => void;
  setSection: (section: NonRefundableFeesSection) => void;
};

export default function NonRefundableFees_v3({ section, setSectionValues, setSection }: Props) {
  const [hasClickedNo, setHasClickedNo] = useState(false);

  function setCustomAmountValidation(section: Section, index: number): any {
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, Number(index));
    section.setCustomFieldValidator(fieldName, isFeeAmountValid.bind(this, fieldName));
  }

  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: NonRefundableFeesSection,
    onIndexAdded: setCustomAmountValidation,
  });

  function isFeeAmountValid(feeFieldName: string, amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > NON_REFUNDABLE_FEES.BOUNDS.MAX_FEE_AMOUNT) {
      section
        ?.getField(feeFieldName)
        ?.setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(NON_REFUNDABLE_FEES.BOUNDS.MAX_FEE_AMOUNT));
      return false;
    }
    return true;
  }

  function onEnableNonRefundableFees(): void {
    const updatedSection = section.clone();
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.IS_ENABLED, true);
    updatedSection.addOneIndex();

    const index = updatedSection.getTotalIndexes() - 1;
    setCustomAmountValidation(updatedSection, index);

    setSection(updatedSection);
  }

  function onRemoveAll(): void {
    setHasClickedNo(true);
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.IS_ENABLED, false);
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_SIGN, false);
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_START, false);
    setSection(updatedSection);
  }

  function setAmount(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setName(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function onPayDueClick(event: any): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_SIGN]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_SIGN,
      [NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_START]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_LEASE_START,
    });
  }

  function renderFee(index: number): React.ReactNode {
    const nameField = section.getField(Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.NAME, index));
    const amountField = section.getField(Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, index));

    return (
      <Spacer marginBottom="sm">
        {index > 0 && (
          <Row paddingY="xs">
            <Col xs={8}>
              <HeaderContainer>
                <Text fontType="bodyHeading">Non-refundable fee #{index + 1}</Text>
              </HeaderContainer>
            </Col>
            <Col xs={4} align="flex-end">
              <HeaderContainer data-index={index} data-test="delete-fee" onClick={onDeleteOne}>
                <TextButton>Delete</TextButton>
              </HeaderContainer>
            </Col>
          </Row>
        )}
        <FormField marginBottom="xs">
          <Label id={`name-${index}`}>{NON_REFUNDABLE_FEES.LABEL.NAME}</Label>
          <Input
            aria-labelledby={`name-${index}`}
            data-index={index}
            data-test="name"
            value={nameField.enteredValue}
            placeholder="(example: Move-in fee)"
            onChange={setName}
          />
        </FormField>
        <FormField
          error={amountField.hasError}
          label={<Label id={`amount-${index}`}>{NON_REFUNDABLE_FEES.LABEL.AMOUNT}</Label>}
          control={
            <AdornedInput
              inputId={`AdornedInput-amount-${index}`}
              leftAdornment={<Adornment>$</Adornment>}
              input={
                <CurrencyInput
                  aria-labelledby={`amount-${index}`}
                  data-index={index}
                  data-test="amount"
                  value={amountField.enteredValue}
                  placeholder={CURRENCY_INPUT_PLACEHOLDER}
                  onChange={setAmount}
                  onBlur={setAmount}
                />
              }
            />
          }
          description={amountField.hasError && <FormHelp>{amountField.getErrorMessage()}</FormHelp>}
        />
      </Spacer>
    );
  }

  const totalNonRefundableFees = section.getTotalIndexes();
  const isEnabled = isYesNoEnabledForNumberedFields(section, 'nonRefundableFees0_isEnabled', hasClickedNo);

  if (totalNonRefundableFees == null) {
    return null;
  }

  return (
    <>
      <Text fontType="h6">Will there be non-refundable fees?</Text>
      <Paragraph marginBottom="sm">
        Examples include administration and move-in fees that are collected at signing.
      </Paragraph>
      <Spacer marginBottom={totalNonRefundableFees > 0 ? 'sm' : 0}>
        <YesAndNo yesHandler={onEnableNonRefundableFees} noHandler={onRemoveAll} yesActive={isEnabled} />
      </Spacer>
      {totalNonRefundableFees > 0 && (
        <>
          <Spacer marginBottom="sm">
            <Paragraph marginBottom="xs">
              The total of these fees should be less than one month’s rent. Do not include pet fees, which are listed
              elsewhere.
            </Paragraph>
            <FeePaymentTime
              onChange={onPayDueClick}
              firstChecked={section.getFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_SIGN)}
              secondChecked={section.getFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_START)}
              section={section}
              label="When are the additional non-refundable fees due?"
            />
          </Spacer>
          <CenteredHR />
        </>
      )}
      {range(0, totalNonRefundableFees).map((index: number) => (
        <React.Fragment key={index}>
          {index > 0 && <CenteredHR />}
          {renderFee(index)}
        </React.Fragment>
      ))}
      <Center>
        {totalNonRefundableFees < MAXIMUM_FEES ? (
          totalNonRefundableFees > 0 && (
            <TextButton data-test="add-another" icon={<IconPlusOutline />} onClick={onAddOne}>
              Add another non-refundable fee
            </TextButton>
          )
        ) : (
          <Text>
            <Center>
              <span data-test="maximum-reached">
                You’ve added {MAXIMUM_FEES} non-refundable fees, which is the maximum allowed.
              </span>
            </Center>
          </Text>
        )}
      </Center>
    </>
  );
}

NonRefundableFees_v3.description = 'Choose when NRF is due';
